import { computed, reactive, ref, toValue, type MaybeRef } from 'vue'
import { useI18n } from 'vue-i18n'
import usePut from '@/core/composables/api/usePut'
import { ApiRequestStatus } from '@/core/models/api/api-request-status'
import { useSnackStore } from '@/stores/snack.store'
import type { ErrorMap, StartAssessmentFormData } from './use-start-assessment'
import type { AssessmentDetail } from './use-assessment'

export const useEditAssessment = (
  assessment: MaybeRef<AssessmentDetail>,
  onClose: () => Promise<void> | void,
) => {
  const { t } = useI18n()
  const snackStore = useSnackStore()

  const formData = reactive<StartAssessmentFormData>({
    title: undefined,
    message: undefined,
    feedbackTopicId: undefined,
    coacheeIds: [],
    peerIds: [],
    includeSelfFeedback: false,
    questionGroupIds: [],
  })

  const errors = ref<ErrorMap>(new Map())

  const validateForm = () => {
    errors.value.clear()

    if (formData.coacheeIds.length === 0) {
      errors.value.set('coacheeIds', t('feedback.formValidation.members'))
    }

    if (!formData.feedbackTopicId) {
      errors.value.set('feedbackTopicId', t('feedback.formValidation.templates'))
    }

    if (formData.peerIds.length === 0 && !formData.includeSelfFeedback) {
      errors.value.set('peerIds', t('feedback.formValidation.members'))
    }
  }

  const isFormValid = computed(() => {
    if (formData.coacheeIds.length === 0) {
      return false
    }
    if (!formData.feedbackTopicId) {
      return false
    }
    return !(formData.peerIds.length === 0 && !formData.includeSelfFeedback)
  })

  const resetForm = () => {
    const currentAssessment = toValue(assessment)
    formData.title = currentAssessment.title ?? undefined
    formData.message = currentAssessment.message ?? undefined
    formData.feedbackTopicId = currentAssessment.feedbackTopic.id
    formData.coacheeIds = currentAssessment.coachees.map((coachee) => coachee.member.id)
    formData.peerIds = currentAssessment.peers.map((peer) => peer.id)
    formData.includeSelfFeedback = currentAssessment.includeSelfFeedback
    formData.questionGroupIds = []
    errors.value.clear()
  }

  resetForm()

  const { mutate: updateAssessment, status: updateAssessmentStatus } = usePut<
    void,
    UpdateAssessmentPayload
  >(`assessment/${toValue(assessment).id}`)

  const saveAssessmentEdits = async () => {
    validateForm()
    if (errors.value.size > 0) {
      return
    }

    await updateAssessment({
      title: formData.title,
      message: formData.message,
      feedbackTopicId: formData.feedbackTopicId,
      coacheeIds: formData.coacheeIds,
      peerIds: formData.peerIds,
      includeSelfFeedback: formData.includeSelfFeedback,
    })

    if (updateAssessmentStatus.value === ApiRequestStatus.Success) {
      await onClose()
      snackStore.showInfo('Assessment wurde aktualisiert.')
      return
    }

    snackStore.showError('Assessment konnte nicht aktualisiert werden.')
  }

  const isSaving = computed(() => updateAssessmentStatus.value === ApiRequestStatus.Loading)

  return {
    errors,
    isFormValid,
    saveAssessmentEdits,
    formData,
    resetForm,
    isSaving,
  }
}

type UpdateAssessmentPayload = Pick<
  StartAssessmentFormData,
  'title' | 'message' | 'feedbackTopicId' | 'coacheeIds' | 'peerIds' | 'includeSelfFeedback'
>
